import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider, focusManager } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";
import App from "@/App";
import { AuthProvider } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";

// Iter-241: TanStack Query v5's default focus manager listens to
// `visibilitychange` only, so plain `window` focus events (e.g. Alt+Tab
// back to the tab without changing visibility) don't trigger a refetch.
// Admins renaming/adding a category expect the site to freshen when the
// operator returns focus. Wire an explicit `focus` + `visibilitychange`
// listener so `refetchOnWindowFocus: true` behaves as advertised.
if (typeof window !== "undefined") {
  focusManager.setEventListener((handleFocus) => {
    const onFocus = () => handleFocus(true);
    const onVisibility = () => handleFocus(document.visibilityState !== "hidden");
    window.addEventListener("focus", onFocus);
    // Per the Page Visibility API, `visibilitychange` is fired on the
    // Document, not the Window — attaching to window silently no-ops on
    // real tab-switch/OS-suspend paths (iteration_68 RCA).
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <I18nProvider>
            <App />
            <Toaster position="top-center" richColors closeButton />
            </I18nProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
